import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Upload,
  Form,
  Col,
  message,
  Tooltip,
  Typography,
} from 'antd';
import { FileUnknownOutlined, InboxOutlined, QuestionCircleOutlined } from '@ant-design/icons';

const Dragger = ({
  rules,
  name,
  label,
  allowedExtensions,
  limitMB,
  multiple,
  disabled,
  tooltip,
  style,
}) => {
  const form = Form.useFormInstance();
  const [fileList, setFileList] = useState([]);
  const accept = !allowedExtensions.length ? '*/*' : allowedExtensions.map((e) => `.${e}`).join(',');
  const formats = allowedExtensions.join(', ').toUpperCase();

  useEffect(() => {
    if (form) setFileList(form.getFieldValue(name)?.fileList || []);
    // eslint-disable-next-line
  }, []);

  const beforeUpload = (file, files) => {
    // eslint-disable-next-line no-param-reassign
    files = files.filter((f) => !fileList.find((e) => e.name === f.name));
    const isValidExt = !allowedExtensions.length || (!multiple
      ? allowedExtensions.includes(file.name.split('.').pop())
      : files.every((f) => allowedExtensions.includes(f.name.split('.').pop())));

    const isLmt = !multiple ? (file.size / 1024 / 1024) < limitMB
      : files.every((f) => (f.size / 1024 / 1024) < limitMB);
    if (!isLmt) {
      message.warn(`${multiple ? 'Cada' : 'El'} archivo no debe sobrepasar los ${limitMB}MB`);
    }

    if (!isValidExt) {
      message.warn(`Solo se admiten archivos en formato ${formats}`);
    }

    if (isValidExt && isLmt) {
      if (multiple) {
        const newFileList = [...fileList, ...files];
        setFileList(newFileList);
        form.setFieldsValue({ [name]: { fileList: newFileList } });
        message.success(`${files.length} archivo${files.length > 1 ? 's' : ''} agregado${files.length > 1 ? 's' : ''} correctamente`);
      } else {
        const newFileList = [file];
        setFileList(newFileList);
        form.setFieldsValue({ [name]: { fileList: newFileList } });
        message.success('Archivo agregado correctamente');
      }
    }
    return false;
  };

  return (
    <Col span={24}>
      <Form.Item
        name={name}
        label={(
          <>
            <Typography.Text style={{ marginRight: 5 }}>{label}</Typography.Text>
            <Tooltip title={allowedExtensions.length && `Solo se admiten archivos en formato ${formats}`}>
              <FileUnknownOutlined style={{ marginLeft: 10 }} />
            </Tooltip>
            {typeof (tooltip?.title || tooltip) === 'string' && (
              <Tooltip title={tooltip?.title || tooltip}>
                <QuestionCircleOutlined />
              </Tooltip>
            )}
          </>
        )}
        rules={rules}
        style={style}
        valuePropName="fileList"
      >
        <Upload.Dragger
          disabled={disabled}
          beforeUpload={beforeUpload}
          className="custom-dragger"
          onRemove={(file) => {
            const newFileList = fileList.filter((e) => e.uid !== file.uid);
            setFileList(newFileList);
            form.setFieldsValue({ [name]: { fileList: newFileList } });
          }}
          fileList={Array.from(fileList)}
          accept={accept}
          multiple={!!multiple}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Haga click o arrastre el documento en esta área.
          </p>
          <p className="ant-upload-hint">
            Soporte para subir
            {' '}
            {!multiple && 'un'}
            {multiple && typeof multiple === 'number' && multiple}
            {multiple && typeof multiple !== 'number' && 'varios'}
            {' '}
            archivo
            {multiple && 's'}
            {allowedExtensions.length > 0 && ` en formato${allowedExtensions.length > 1 ? 's' : ''} ${formats}`}
            {limitMB && ` (máximo ${limitMB}MB por archivo)`}
            .
          </p>
        </Upload.Dragger>
      </Form.Item>
    </Col>
  );
};

Dragger.propTypes = {
  allowedExtensions: PropTypes.arrayOf(PropTypes.string),
  rules: PropTypes.arrayOf(PropTypes.shape({})),
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  limitMB: PropTypes.number,
  multiple: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  disabled: PropTypes.bool,
  tooltip: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  style: PropTypes.shape({}),
};

Dragger.defaultProps = {
  allowedExtensions: [],
  rules: [],
  limitMB: 20,
  multiple: false,
  disabled: false,
  tooltip: null,
  style: null,
};

export default Dragger;
